import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from '../hooks/use-toast';
import { getCartLineKey, getCartDisplayName } from '../utils/cartLine';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem('tribaldash_cart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        setCartItems(
          Array.isArray(parsed)
            ? parsed.map((item) => ({
                ...item,
                cartKey: item.cartKey || getCartLineKey(item),
              }))
            : []
        );
      } catch {
        setCartItems([]);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('tribaldash_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    const soldOut =
      product.inStock === false || product.available === false || product.quantity === 0;
    if (soldOut) {
      toast({
        title: 'Sold out',
        description: `${product.name} is currently unavailable.`,
        variant: 'destructive',
      });
      return;
    }

    const cartKey = getCartLineKey(product);
    const displayName = getCartDisplayName(product);

    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.cartKey === cartKey);

      if (existingItem) {
        toast({
          title: 'Updated cart',
          description: `${displayName} quantity updated`,
        });
        return prevItems.map((item) =>
          item.cartKey === cartKey
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      toast({
        title: 'Added to cart',
        description: `${displayName} has been added to your cart`,
      });
      return [
        ...prevItems,
        {
          ...product,
          cartKey,
          quantity,
        },
      ];
    });
  };

  const removeFromCart = (cartKey) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.cartKey !== cartKey));
    toast({
      title: 'Removed from cart',
      description: 'Item has been removed from your cart',
    });
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.cartKey === cartKey ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('tribaldash_cart');
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
